import * as React from "react"
import 'intersection-observer';
import { InView } from 'react-intersection-observer';
import { Action, IOWidget, mk_widget, Widget, AsyncState, Pair, mk_pair } from "widgets-for-react"
import { CMSState, Id, ImageSize } from "../../../../cms/blocks/types.gen";
import { getClassName } from "../../../../cms/blocks/utils.gen";
import { List, Map } from "immutable";
import { ImageContainer } from "./ImageContainer";
import { blockInviewHandler } from "../../../utils/inview_utils";
import { imageGalleryBlockData } from "../types.mod";
import { PublicSite_CustomPageData } from "../../../application.mod";
import { WordPress } from "../../../../wordpress/wordpress_api";
import { PublicSiteState } from "../../../../public_site.main.mod";
import { getImageAttribute } from "../../../utils/extra_images.mod";
import { decodeHTMLEntities, gtmPush } from "../../../../../helpers";

export type ImageGalleryBlockItem = {
  image: WordPress.MediaResponse
  caption: string
}

export type ImageGalleryAttributes = {
  blockName: 'vidda/gallery'
  title: string
  intro: string
  items: Array<ImageGalleryBlockItem>
}

export interface ImageGalleryInterface {
  blockName: 'vidda/gallery'
  attrs: ImageGalleryAttributes
}

export type ImageGalleryBlockProps<extra_images> = ImageGalleryAttributes


export type ImageGalleryBlockStyle = Partial<{
  block_classname: string
  inview_classname: string
  inner_classname: string
  content_classname: string
  header_classname: string
  title_classname: string
  intro_classname: string
  gallery_list_classname: string
}> & ImageGalleryItemStyle

export type ImageGalleryItemStyle = Partial<{
  gallery_item_classname: string
  gallery_button_classname: string
  gallery_image_classname: string
  gallery_toggle_classname: string
  lightbox_classname: string
  lightbox_inner_classname: string
  lightbox_figure_classname: string
  lightbox_visual_classname: string
  lightbox_image_classname: string
  lightbox_figcaption_classname: string
  lightbox_caption_classname: string
  lightbox_source_classname: string
  lightbox_nav_classname: string
  lightbox_nav_inner_classname: string
  lightbox_controls_classname: string
  lightbox_controls_inner_classname: string
  lightbox_next_classname: string
  lightbox_prev_classname: string
  lightbox_close_classname: string
  icon_close_id: string
  icon_previous_id: string
  icon_next_id: string
}>

export function ImageGalleryBlock<customBlockData, extra_images>(
  props: ImageGalleryBlockProps<extra_images>,
  options?: { style: ImageGalleryBlockStyle; key?: string }
): (s: CMSState<customBlockData, extra_images, PublicSite_CustomPageData>) => IOWidget<PublicSiteState, Action<CMSState<customBlockData, extra_images, PublicSite_CustomPageData>>> {
  return (s: CMSState<customBlockData, extra_images, PublicSite_CustomPageData>) => (public_state: PublicSiteState) => {
    return mk_widget({
      run: _ => (
        <section className="block block--background block--gallery grid--block ">
          <div className="block__inner">
            { (props.title || props.intro) && (
              <header className="block__header">
                {props.title && 
                  <h2 className="block__title typo--3xl">{decodeHTMLEntities(props.title)}</h2>
                }
                {props.intro && 
                  <div className="block__intro typo--xs" dangerouslySetInnerHTML={{__html: props.intro}} /> 
                }
              </header>
            )}
            { props.items && 
              <div className={'block__content'}>
                <ImageGallery items={props.items} />
              </div>
            }
          </div>
        </section>
      )
    })
  }
}

type ImageGalleryState = {
  currentImage?: number
  open: boolean
}

type ImageGalleryProps = {
  items: Array<ImageGalleryBlockItem>
}

class ImageGallery extends React.Component<ImageGalleryProps, ImageGalleryState> {
  constructor(props) {
    super(props)

    this.state = {
      open: false
    }

    this.prevImage = this.prevImage.bind(this)
    this.nextImage = this.nextImage.bind(this)
  }

  prevImage() {
    let newIndex: number = this.state.currentImage > 1 ? this.state.currentImage - 1 : 0
    this.setState({...this.state, open: true, currentImage: newIndex })
  }

  nextImage() {
    let newIndex: number = this.state.currentImage < this.props.items.length ? this.state.currentImage + 1 : this.props.items.length
    this.setState({...this.state, open: true, currentImage: newIndex})
  }
  
  render() {
    const currentImage: ImageGalleryBlockItem = this.state.open && this.state.currentImage !== null ? this.props.items[this.state.currentImage] : null
    return (
      <>
        <div className="gallery" data-gallery-show-more={this.props.items.length > 5 ? true : null}>
          { this.props.items.slice(0, 5).map((item: ImageGalleryBlockItem, index: number) =>
            <div 
              key={`gallery-${item.image.id}`}
              className={`gallery__item`} 
              data-gallery-more={`+${this.props.items.length - (index + 1)}`}
            >
              <figure 
                className="gallery__visual" 
                style={{ backgroundImage: `url('${getImageAttribute(item.image, 'square', 'source_url')}')` }}
                onClick={() => {
                  gtmPush({
                    event: "image_galery", 
                    interaction: "expand_images"
                  })
                  this.setState({...this.state, currentImage: index, open: true})
                }}>

                <img
                  src={getImageAttribute(item.image, 'square', 'source_url')}
                  width={getImageAttribute(item.image, 'square', 'width')}
                  height={getImageAttribute(item.image, 'square', 'height')}
                  className="gallery__image"
                  alt={item.image.alt_text ? item.image.alt_text : ''}
                />
              </figure>
            </div>
          )}
        </div>
        { currentImage && 
          <>
            <div className="popup__overlay">
              <div className="popup">
                <figure className="popup__visual">
                  <img
                    src={getImageAttribute(currentImage.image, 'large', 'source_url')}
                    width={getImageAttribute(currentImage.image, 'large', 'width')}
                    height={getImageAttribute(currentImage.image, 'large', 'height')}
                    className="popup__image"
                    alt={currentImage.image.alt_text ? currentImage.image.alt_text : ''}
                  />
                  { currentImage.caption && <figcaption className="popup__caption typo--2xs">{currentImage.caption}</figcaption> }
                </figure>
              </div>
              <button disabled={this.state.currentImage == 0} onClick={this.prevImage} className="popup__prev"></button>
              <button disabled={this.state.currentImage == this.props.items.length - 1} onClick={this.nextImage} className="popup__next"></button>
              <button onClick={_ => { this.setState({ open: false, currentImage: null })}} className="popup__close"></button>
            </div>
          </>
        }
      </>
    )
  }
}